import { useMemo, useState } from 'react';
import { db } from '../../lib/firebase';
import { removeDoc, saveDoc } from '../../lib/firestore-write';
import { useOrderedCol } from '../../lib/hooks';
import type { ToolDoc } from '../../lib/data-model';
import { ConfirmDialog, EmptyState, Loading, Modal, toast } from '../ui';
import { AdminToolbar, DeleteBtn, EditBtn, HiddenBtn, NewBtn, SwitchRow, SwapBar, errOf, useSaver } from './ListAdmin';

const TINTS = ['var(--t-green)', 'var(--t-plum)', 'var(--t-amber)', 'var(--t-blue)', 'var(--t-indigo)', 'var(--t-terra)'] as const;
const INKS = ['var(--c-green)', 'var(--c-plum)', 'var(--c-amber)', 'var(--c-blue)', 'var(--c-indigo)', 'var(--c-terra)'] as const;

export function ToolsTab() {
  const tools = useOrderedCol<ToolDoc>('tools');
  const [editor, setEditor] = useState<{ id?: string } | null>(null);
  const [deleting, setDeleting] = useState<(ToolDoc & { id: string }) | null>(null);
  const [query, setQuery] = useState('');

  if (tools.loading) return <Loading label="Loading tools…" />;

  const toggle = (t: ToolDoc & { id: string }) =>
    saveDoc(db, 'tools', t.id, { ...t, hidden: !t.hidden })
      .then(() => toast('Saved', 'ok'))
      .catch(e => toast(errOf(e), 'err'));

  const del = async () => {
    if (!deleting) return;
    try {
      await removeDoc(db, 'tools', deleting.id);
      toast('Deleted', 'ok');
    } catch (e) {
      toast(errOf(e), 'err');
    }
  };

  const order = tools.data.length ? Math.max(...tools.data.map(t => t.order)) + 1 : 0;

  const shown = tools.data.filter(t => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return `${t.name} ${t.tag} ${t.blurb}`.toLowerCase().includes(q);
  });

  return (
    <div className="panel">
      <div className="admin-wrap">
        <AdminToolbar label={`${tools.data.length} TOOL${tools.data.length === 1 ? '' : 'S'}`} query={query} onQuery={setQuery}>
          <NewBtn onClick={() => setEditor({})}>+ New tool</NewBtn>
        </AdminToolbar>

        {tools.data.length === 0 ? (
          <EmptyState glyph="⚙" title="No tools yet" body="Add the study utilities shown on the Tools page." />
        ) : shown.length === 0 ? (
          <EmptyState glyph="⌕" title="No matches" body={`Nothing matches “${query}”. Try a different name or tag.`} />
        ) : (
          <div className="admin-grid">
            {shown.map(t => (
              <div key={t.id} className={`admin-card${t.hidden ? ' off' : ''}`}>
                <div className="card-top">
                  <span
                    className="glyph-tile"
                    style={{ background: t.iconBg ?? 'var(--t-green)', color: t.iconFg ?? 'var(--c-green)', fontFamily: 'var(--font-body)' }}
                    aria-hidden
                  >
                    {t.glyph ?? '◈'}
                  </span>
                  <span className="grow" />
                  <span className="pill line">{t.tag}</span>
                  <span className={`dot ${t.hidden ? 'off' : 'on'}`} title={t.hidden ? 'Hidden' : 'Published'} />
                </div>
                <h3>{t.name}</h3>
                <p>{t.blurb || 'No blurb yet.'}</p>
                <div className="card-foot">
                  <SwapBar coll="tools" list={tools.data} id={t.id} hidden={t.hidden} label={t.name} />
                  <HiddenBtn hidden={!!t.hidden} onClick={() => void toggle(t)} />
                  <EditBtn onClick={() => setEditor({ id: t.id })} />
                  <DeleteBtn onClick={() => setDeleting(t)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {editor && (
        <ToolForm
          order={order}
          existing={editor.id ? tools.data.find(t => t.id === editor.id) : undefined}
          onClose={() => setEditor(null)}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title="Delete tool?"
          confirmLabel="Delete tool"
          body={<p>Delete <strong>{deleting.name}</strong>? This cannot be undone.</p>}
          onConfirm={del}
          onCancel={() => setDeleting(null)}
        />
      )}
    </div>
  );
}

function ColorSelect({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: readonly string[];
  onChange: (v: string) => void;
}) {
  return (
    <div className="field">
      <label>{label}</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <select value={value} onChange={e => onChange(e.target.value)} style={{ flex: 1 }}>
          {options.map(o => (
            <option key={o} value={o}>
              {o.replace('var(', '').replace(')', '').replace(/^(t|c)-/, '$1 · ')}
            </option>
          ))}
        </select>
        <span style={{ width: 26, height: 26, borderRadius: 9, background: value, flex: 'none', border: '1px solid var(--line)' }} aria-hidden />
      </div>
    </div>
  );
}

function ToolForm({ order, existing, onClose }: { order: number; existing?: ToolDoc & { id: string }; onClose: () => void }) {
  const id = useMemo(() => existing?.id ?? `tool-${Date.now().toString(36)}`, [existing]);
  const [name, setName] = useState(existing?.name ?? '');
  const [tag, setTag] = useState(existing?.tag ?? '');
  const [blurb, setBlurb] = useState(existing?.blurb ?? '');
  const [url, setUrl] = useState(existing?.url ?? '');
  const [glyph, setGlyph] = useState(existing?.glyph ?? '✦');
  const [iconBg, setIconBg] = useState(existing?.iconBg ?? 'var(--t-green)');
  const [iconFg, setIconFg] = useState(existing?.iconFg ?? 'var(--c-green)');
  const [hidden, setHidden] = useState(existing?.hidden ?? false);

  const { errors, saving, save } = useSaver(
    'tools',
    id,
    () => {
      const d: Record<string, unknown> = { order, name, tag, blurb, url, hidden };
      if (glyph.trim()) d.glyph = glyph.trim();
      if (iconBg) d.iconBg = iconBg;
      if (iconFg) d.iconFg = iconFg;
      return d;
    },
    onClose
  );

  return (
    <Modal
      title={existing ? 'Edit tool' : 'New tool'}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void save()} disabled={saving}>{saving ? 'Saving…' : 'Save tool'}</button>
        </>
      }
    >
      <div className="row2">
        <div className="field">
          <label>Name</label>
          <input type="text" value={name} onChange={e => setName(e.target.value)} />
          {errors.name ? <div className="error">{errors.name}</div> : null}
        </div>
        <div className="field">
          <label>Tag</label>
          <input type="text" value={tag} onChange={e => setTag(e.target.value)} placeholder="Grapher" />
          {errors.tag ? <div className="error">{errors.tag}</div> : null}
        </div>
      </div>

      <div className="field">
        <label>Blurb</label>
        <textarea value={blurb} onChange={e => setBlurb(e.target.value)} />
        {errors.blurb ? <div className="error">{errors.blurb}</div> : null}
      </div>

      <div className="field">
        <label>URL (https)</label>
        <input type="url" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://…" />
        {errors.url ? <div className="error">{errors.url}</div> : null}
      </div>

      <div className="field">
        <label>Glyph</label>
        <input type="text" maxLength={2} value={glyph} onChange={e => setGlyph(e.target.value)} />
        {errors.glyph ? <div className="error">{errors.glyph}</div> : null}
      </div>

      <div className="row2">
        <ColorSelect label="Icon background" value={iconBg} options={TINTS} onChange={setIconBg} />
        <ColorSelect label="Icon colour" value={iconFg} options={INKS} onChange={setIconFg} />
      </div>

      <SwitchRow label="Hidden" on={hidden} onToggle={() => setHidden(v => !v)} />
    </Modal>
  );
}
